import { memo, useLayoutEffect, useRef, useState } from "react";

import { Button, Textfield } from "@similarweb/ui-components";

import { ProModal } from "pro-features/components/Modals/src/ProModal";
import type { TranslationReplacements } from "app/filters/i18nFilter";
import { translate } from "app/filters/i18nFilter";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import { compareNamesAsc } from "app/pages/sales-intelligence/sub-modules/advanced-search/helpers/common";
import { isSavedSearchNameLongEnough } from "app/pages/sales-intelligence/sub-modules/advanced-search/helpers/saved-searches";
import type { Translatable } from "app/pages/sales-intelligence/types";

import {
    CUSTOM_MODAL_STYLES,
    StyledButtonsContainer,
    StyledHeader,
    StyledModalContent,
    StyledNameInputContainer,
    StyledNameInputDescription,
    StyledNameInputLabel,
    StyledSubtitle,
    StyledTitle,
} from "./styles";

type SaveSearchModalProps = {
    isOpened: boolean;
    isSubmitting: boolean;
    textVariant?: string;
    defaultSearchName?: Readonly<Translatable>;
    onCancel(): void;
    onSubmit(name: string, isDefaultName: boolean): void;
};

const SaveSearchModal = (props: SaveSearchModalProps) => {
    const { isOpened, isSubmitting, textVariant, defaultSearchName, onCancel, onSubmit } = props;

    const defaultSearchNameTranslation =
        typeof defaultSearchName === "undefined"
            ? ""
            : translate(defaultSearchName.key, defaultSearchName.replacements as Readonly<TranslationReplacements>);

    const [name, setName] = useState(defaultSearchNameTranslation);
    const textFieldRef = useRef<Textfield | null>(null);

    const isNameLongEnough = isSavedSearchNameLongEnough(name);

    useLayoutEffect(() => {
        if (!isOpened || typeof defaultSearchName === "undefined") {
            return;
        }

        const timeout = setTimeout(() => {
            textFieldRef.current?.select();
        });

        return () => {
            clearTimeout(timeout);
        };
    }, [isOpened]);

    const getKeyWithModifier = (key: string, defaultVariant?: string): string => {
        const keyModifier = textVariant ?? defaultVariant;

        if (typeof keyModifier !== "undefined") {
            return key + `.${keyModifier}`;
        }

        return key;
    };

    const checkIsDefaultName = (defaultName: string, name: string) => {
        const isDefaultNameDefined = defaultName.length > 0;

        if (isDefaultNameDefined) {
            return compareNamesAsc({ name: defaultName }, { name }) === 0;
        }

        return false;
    };

    const handleSubmit = () => {
        if (isNameLongEnough) {
            const isDefaultName = checkIsDefaultName(defaultSearchNameTranslation, name);

            onSubmit(name.trim(), isDefaultName);
        }
    };

    return (
        <ProModal
            isOpen={isOpened}
            showCloseIcon={false}
            shouldCloseOnOverlayClick={false}
            customStyles={CUSTOM_MODAL_STYLES}
        >
            <StyledModalContent>
                <StyledHeader>
                    <I18n
                        data-automation="title-text"
                        element={StyledTitle}
                        translatable={{
                            key: getKeyWithModifier("si.components.save_new_search_modal.title"),
                        }}
                    />
                    <I18n
                        data-automation="subtitle-text"
                        element={StyledSubtitle}
                        translatable={{ key: "si.components.save_new_search_modal.subtitle" }}
                    />
                </StyledHeader>
                <StyledNameInputContainer>
                    <I18n
                        data-automation="name-input-label-text"
                        element={StyledNameInputLabel}
                        translatable={{
                            key: "si.components.save_new_search_modal.text_field_label",
                        }}
                    />
                    <Textfield
                        ref={textFieldRef}
                        autoFocus
                        maxLength={100}
                        defaultValue={defaultSearchNameTranslation}
                        onChange={setName}
                        dataAutomation="save-new-search-modal-input"
                    />
                    <I18n
                        data-automation="name-input-description-text"
                        element={StyledNameInputDescription}
                        translatable={{
                            key: "si.components.save_new_search_modal.text_field_description",
                        }}
                    />
                </StyledNameInputContainer>
                <StyledButtonsContainer>
                    <Button
                        type="flat"
                        onClick={onCancel}
                        isDisabled={isSubmitting}
                        dataAutomation="save-new-search-modal-button-cancel"
                    >
                        {translate(getKeyWithModifier("si.components.save_new_search_modal.button.cancel"))}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        isLoading={isSubmitting}
                        isDisabled={!isNameLongEnough || isSubmitting}
                        dataAutomation="save-new-search-modal-button-submit"
                    >
                        {translate(getKeyWithModifier("si.components.save_new_search_modal.button.submit"))}
                    </Button>
                </StyledButtonsContainer>
            </StyledModalContent>
        </ProModal>
    );
};

export default memo(SaveSearchModal);
